import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import logo from "../../assets/img/header_logo.png";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#222",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    borderBottom: "1 solid #e5e5e5",
    paddingBottom: 10,
    marginBottom: 15,
  },

  logo: {
    height: 100,
    objectFit: "contain",
  },

  headerText: {
    flexDirection: "column",
    marginLeft: 10,
  },

  title: {
    fontSize: 18,
    fontWeight: 700,
  },

  subtitle: {
    fontSize: 10,
    color: "#666",
    marginTop: 2,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 12,
    marginBottom: 6,
    borderBottom: "1 solid #eee",
    paddingBottom: 3,
  },

  row: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  col: {
    width: "50%",
    paddingRight: 6,
    marginBottom: 4,
  },

  label: {
    fontWeight: 700,
  },

  card: {
    border: "1 solid #eee",
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
  },

  fileBox: {
    border: "1 dashed #ccc",
    padding: 8,
    marginBottom: 8,
  },

  image: {
    width: 150,
    height: 150,
    objectFit: "cover",
    marginTop: 5,
  },
});

const isImageFile = (file: File | null) => {
  return !!file && file.type.startsWith("image/");
};

const FilePreview = ({ file, label }: { file: File | null; label: string }) => {
  if (!file) return null;

  // IMAGE
  if (isImageFile(file)) {
    const src = URL.createObjectURL(file);

    return (
      <View style={{ marginBottom: 15 }}>
        <Text style={{ marginBottom: 5 }}>{label}</Text>

        <Image src={src} style={styles.image} />
      </View>
    );
  }

  // PDF
  if (file.type === "application/pdf") {
    return (
      <View style={styles.fileBox}>
        <Text style={{ fontWeight: 700 }}>{label}</Text>

        <Text>PDF Attached: {file.name}</Text>
      </View>
    );
  }

  return null;
};

const DownloadApplicationForm = ({ formData }: { formData: any }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />

          <View style={styles.headerText}>
            <Text style={styles.title}>Application Form</Text>
          </View>
        </View>

        {/* PROGRAM DETAILS */}
        <Text style={styles.sectionTitle}>Program Information</Text>

        <View style={styles.row}>
          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Studies: </Text>
              {formData.studies}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>First Priority: </Text>
              {formData.firstPriorityProgram}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Second Priority: </Text>
              {formData.secondPriorityProgram}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Third Priority: </Text>
              {formData.thirdPriorityProgram}
            </Text>
          </View>
        </View>

        {/* PERSONAL INFORMATION */}
        <Text style={styles.sectionTitle}>Personal Information</Text>

        <View style={styles.row}>
          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>First Name: </Text>
              {formData.firstName}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Middle Name: </Text>
              {formData.middleName}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Last Name: </Text>
              {formData.lastName}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Date of Birth: </Text>
              {formData.dob}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Gender: </Text>
              {formData.gender}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Nationality: </Text>
              {formData.nationality}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Ethnicity: </Text>
              {formData.ethnicity}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Email: </Text>
              {formData.email}
            </Text>
          </View>

          <View style={styles.col}>
            <Text>
              <Text style={styles.label}>Phone: </Text>
              {formData.phone}
            </Text>
          </View>
        </View>

        {/* ADDRESS */}
        <Text style={styles.sectionTitle}>Address Information</Text>

        <View style={styles.card}>
          <Text>
            <Text style={styles.label}>Current Address: </Text>
            {formData.currentAddress}
          </Text>

          <Text>
            <Text style={styles.label}>Permanent Address: </Text>
            {formData.permanentAddress}
          </Text>
        </View>

        {/* GUARDIAN */}
        <Text style={styles.sectionTitle}>Guardian Information</Text>

        <View style={styles.card}>
          <Text>
            <Text style={styles.label}>Guardian Name: </Text>
            {formData.guardianName}
          </Text>

          <Text>
            <Text style={styles.label}>Relation: </Text>
            {formData.guardianRelation}
          </Text>
        </View>

        {/* ACADEMIC */}
        <Text style={styles.sectionTitle}>Academic Information</Text>

        <View style={styles.card}>
          <Text>
            <Text style={styles.label}>+2 Institution: </Text>
            {formData.plus2institution}
          </Text>

          <Text>
            <Text style={styles.label}>Year of Graduation: </Text>
            {formData.yearOfGraduation}
          </Text>
        </View>

        {/* INTERNSHIP */}
        <Text style={styles.sectionTitle}>Internship Information</Text>

        <View style={styles.card}>
          <Text>
            <Text style={styles.label}>Organization: </Text>
            {formData.internOrg}
          </Text>

          <Text>
            <Text style={styles.label}>Duration: </Text>
            {formData.internDuration}
          </Text>
        </View>

        {/* FINANCE */}
        <Text style={styles.sectionTitle}>Financial Information</Text>

        <View style={styles.card}>
          <Text>
            <Text style={styles.label}>Finance Plan: </Text>
            {formData.financePlan}
          </Text>

          <Text>
            <Text style={styles.label}>Scholarship: </Text>
            {formData.scholarship}
          </Text>

          <Text>
            <Text style={styles.label}>Scholarship Types: </Text>
            {formData.scholarshipTypes?.join(", ")}
          </Text>
        </View>

        {/* REFEREES */}
        <Text style={styles.sectionTitle}>Referees</Text>

        {formData.referee1Name && (
          <View style={styles.card}>
            <Text>
              <Text style={styles.label}>Name: </Text>
              {formData.referee1Name}
            </Text>

            <Text>
              <Text style={styles.label}>Email: </Text>
              {formData.referee1Email}
            </Text>

            <Text>
              <Text style={styles.label}>Phone: </Text>
              {formData.referee1Mobile}
            </Text>
          </View>
        )}

        {formData.referee2Name && (
          <View style={styles.card}>
            <Text>
              <Text style={styles.label}>Name: </Text>
              {formData.referee2Name}
            </Text>

            <Text>
              <Text style={styles.label}>Email: </Text>
              {formData.referee2Email}
            </Text>

            <Text>
              <Text style={styles.label}>Phone: </Text>
              {formData.referee2Mobile}
            </Text>
          </View>
        )}

        {/* DECLARATION */}
        <Text style={styles.sectionTitle}>Declaration</Text>

        <View style={styles.card}>
          <Text>
            {formData.declaration
              ? "Applicant has accepted the declaration."
              : "Declaration not accepted."}
          </Text>
        </View>

        {/* FILES */}
        <Text style={styles.sectionTitle}>Uploaded Documents</Text>

        <FilePreview file={formData.profileFile} label="Profile Photo" />

        <FilePreview file={formData.citizenshipFile} label="Citizenship" />

        <FilePreview file={formData.cvFile} label="CV" />

        <FilePreview file={formData.sopFile} label="Statement of Purpose" />

        <FilePreview
          file={formData.seeCertificateFile}
          label="SEE Certificate"
        />

        <FilePreview
          file={formData.plus2certificateFile}
          label="+2 Certificate"
        />

        <FilePreview file={formData.academicAwardFile} label="Academic Award" />

        <FilePreview file={formData.otherDegreeFile} label="Other Degree" />

        <FilePreview file={formData.paymentFile} label="Payment Receipt" />
      </Page>
    </Document>
  );
};

export default DownloadApplicationForm;
